import { useState } from 'react';
import './DiffView.css';

interface DiffLine {
    type: 'add' | 'del' | 'context';
    content: string;
    oldLine?: number;
    newLine?: number;
}

interface DiffHunk {
    oldStart: number;
    oldCount: number;
    newStart: number;
    newCount: number;
    lines: DiffLine[];
}

interface DiffFile {
    path: string;
    status: 'added' | 'deleted' | 'modified';
    oldSha?: string;
    newSha?: string;
    additions: number;
    deletions: number;
    hunks?: DiffHunk[];
}

interface DiffViewProps {
    files: DiffFile[];
    stats: {
        additions: number;
        deletions: number;
        changed: number;
    };
}

export function DiffView({ files, stats }: DiffViewProps) {
    const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set(files.map(f => f.path)));

    const toggleFile = (path: string) => {
        const newExpanded = new Set(expandedFiles);
        if (newExpanded.has(path)) {
            newExpanded.delete(path);
        } else {
            newExpanded.add(path);
        }
        setExpandedFiles(newExpanded);
    };

    const getStatusIcon = (status: DiffFile['status']) => {
        switch (status) {
            case 'added':
                return <span className="status-icon added">A</span>;
            case 'deleted':
                return <span className="status-icon deleted">D</span>;
            case 'modified':
                return <span className="status-icon modified">M</span>;
        }
    };

    return (
        <div className="diff-view">
            <div className="diff-header">
                <div className="diff-stats">
                    <span className="stat-item changed">
                        {stats.changed} 个文件变更
                    </span>
                    <span className="stat-item additions">
                        +{Math.round(stats.additions)}
                    </span>
                    <span className="stat-item deletions">
                        -{Math.round(stats.deletions)}
                    </span>
                </div>
            </div>

            <div className="diff-files">
                {files.length === 0 ? (
                    <div className="empty-diff">没有文件变更</div>
                ) : (
                    files.map((file) => (
                        <div key={file.path} className={`diff-file-item ${file.status}`}>
                            <div className="file-header" onClick={() => toggleFile(file.path)}>
                                <div className="file-info">
                                    <span className="expand-icon">
                                        {expandedFiles.has(file.path) ? '▼' : '▶'}
                                    </span>
                                    {getStatusIcon(file.status)}
                                    <span className="file-path">{file.path}</span>
                                </div>
                                <div className="file-stats">
                                    {file.additions > 0 && (
                                        <span className="additions">+{Math.round(file.additions)}</span>
                                    )}
                                    {file.deletions > 0 && (
                                        <span className="deletions">-{Math.round(file.deletions)}</span>
                                    )}
                                </div>
                            </div>

                            {expandedFiles.has(file.path) && (
                                <div className="diff-content">
                                    {file.hunks && file.hunks.length > 0 ? (
                                        file.hunks.map((hunk, hunkIdx) => (
                                            <div key={hunkIdx} className="diff-hunk">
                                                <div className="hunk-header">
                                                    @@ -{hunk.oldStart},{hunk.oldCount} +{hunk.newStart},{hunk.newCount} @@
                                                </div>
                                                <table className="diff-table">
                                                    <tbody>
                                                        {hunk.lines.map((line, lineIdx) => (
                                                            <tr key={lineIdx} className={`diff-line ${line.type}`}>
                                                                <td className="line-num old">
                                                                    {line.type !== 'add' ? line.oldLine : ''}
                                                                </td>
                                                                <td className="line-num new">
                                                                    {line.type !== 'del' ? line.newLine : ''}
                                                                </td>
                                                                <td className="line-sign">
                                                                    {line.type === 'add' && '+'}
                                                                    {line.type === 'del' && '-'}
                                                                    {line.type === 'context' && ' '}
                                                                </td>
                                                                <td className="line-content">
                                                                    <pre>{line.content}</pre>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="diff-placeholder">
                                            <p>
                                                {file.status === 'added' && '新增文件'}
                                                {file.status === 'deleted' && '删除文件'}
                                                {file.status === 'modified' && '二进制文件或无变更内容'}
                                            </p>
                                            {file.oldSha && <code>旧版本: {file.oldSha.substring(0, 7)}</code>}
                                            {file.newSha && <code>新版本: {file.newSha.substring(0, 7)}</code>}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

export default DiffView;

